import React from 'react';
import {
  Box,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
} from '@mui/material';
import { Add } from '@mui/icons-material';
import { Game } from '../../services/gamesService';
import { getGameImageSource } from '../../utils/normalizers/gameImageUrl';

interface CreatePackSectionProps {
  games: Game[];
  selectedGame: string;
  gamesLoading: boolean;
  createPackLoading: boolean;
  onGameChange: (event: any) => void;
  onCreatePack: () => void;
  totalPacks?: number;
  compact?: boolean;
}

export const CreatePackSection: React.FC<CreatePackSectionProps> = ({
  games,
  selectedGame,
  gamesLoading,
  createPackLoading,
  onGameChange,
  onCreatePack,
  totalPacks,
  compact = false,
}) => {
  const formMinWidth = compact ? 260 : 340;
  const formMaxWidth = compact ? 480 : 'none';
  const actionHeight = compact ? 42 : 56;
  const actionWidth = compact ? 170 : 'auto';

  const content = (
    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end', flexWrap: 'wrap', width: '100%' }}>
      <FormControl sx={{ minWidth: formMinWidth, maxWidth: formMaxWidth, flexGrow: 1 }}>
        <InputLabel id="game-select-label">Select Game</InputLabel>
        <Select
          labelId="game-select-label"
          id="game-select"
          value={selectedGame}
          label="Select Game"
          onChange={onGameChange}
          disabled={gamesLoading}
          sx={{
            height: actionHeight,
            minHeight: actionHeight,
            '& .MuiSelect-select': {
              height: '100%',
              display: 'flex',
              alignItems: 'center',
            },
          }}
        >
          {games.map((game) => (
            <MenuItem key={game.id} value={game.id}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <img
                  src={getGameImageSource(game)}
                  alt={game.name}
                  style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }}
                />
                <Box>
                  <Typography variant="body2" fontWeight="bold">
                    {game.name} ({game.state})
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    ${game.cost_per_ticket} • {game.tickets_per_pack} tickets
                  </Typography>
                </Box>
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Button
        variant="contained"
        onClick={onCreatePack}
        disabled={!selectedGame || gamesLoading || createPackLoading}
        startIcon={createPackLoading ? <CircularProgress size={20} /> : <Add />}
        size={compact ? 'medium' : 'large'}
        sx={{
          width: actionWidth,
          height: actionHeight,
          minHeight: actionHeight,
          background: '#34C759',
          '&:hover': {
            background: '#34C759',
          },
          '&:disabled': {
            background: 'linear-gradient(45deg, #f44336, #d32f2f)',
          },
        }}
      >
        {createPackLoading ? 'Creating...' : 'Activate Pack'}
      </Button>
    </Box>
  );

  if (compact) {
    return (
      <Box sx={{ flex: 1, minWidth: 320 }}>
        {content}
      </Box>
    );
  }

  return (
    <Paper  sx={{ p: 1.5, mb: 1.5, bgcolor: 'rgb(248, 249, 251)', display: 'flex', flexDirection: 'row', justifyContent: 'space-between', border: '1px solid #e0e0e0', boxShadow: 'none', borderRadius: '8px' }}>
      <Box>
        <Typography variant="body1" fontWeight={600} fontSize={18}>
        Activate Pack ({totalPacks ?? 0})
        </Typography>
        <Typography variant="body2" color="text.secondary" fontSize={15} fontWeight={400}>
        Select a game to create a new activated pack (default: Scratchers)
        </Typography>
      </Box>
      <Box sx={{ minWidth: 420, flex: 1, ml: 2 }}>
        {content}
      </Box>
    </Paper>
  );
}; 
